import { GeneralApiService } from '@api_patient/general_api.service';
import { Injectable, Scopes } from '@visiba/instantiation';

/**
 * Service to load and distribute Feature Flags in the application.
 *
 * To add a new Feature Flag, add a new property to the "ExpectedFlags"
 * interface in `feature_flags.d.ts`.
 *
 * ### Related modules
 * * useFeatureFlag hook - used in React views for conditional renders.
 */
@Injectable({
	scope: Scopes.Singleton,
})
export class FeatureFlagService<T extends ExpectedFlags = ExpectedFlags> {
	public static checkFlag<T extends ExpectedFlags>(flag: keyof T): Result<boolean, FeatureFlagStoreNotSet> {
		if ((globalThis as typeof window).__visiba_feature_flags__ == null) Err(new FeatureFlagStoreNotSet());

		const retrievedFlag = (globalThis as typeof window).__visiba_feature_flags__[flag as keyof ExpectedFlags];

		return Ok(retrievedFlag ?? false);
	}

	public static setFlag<T extends ExpectedFlags>(flag: keyof T, value: boolean) {
		if ((globalThis as typeof window).__visiba_feature_flags__ == null)
			(globalThis as typeof window).__visiba_feature_flags__ = {} as Record<keyof ExpectedFlags, boolean>;
		(globalThis as typeof window).__visiba_feature_flags__[flag as keyof ExpectedFlags] = value;
	}

	constructor(private readonly generalApiService: GeneralApiService) {
		// Empty
	}

	public async load(urlAlias: string): AsyncResult<typeof window.__visiba_feature_flags__> {
		(globalThis as typeof window).__visiba_feature_flags__ = undefined as never;

		const { ok, err } = await this.generalApiService.getFeatureToggles(urlAlias);
		if (err) return Err(err);

		(globalThis as typeof window).__visiba_feature_flags__ = {
			_NOOP_EXAMPLE_DO_NOT_USE: false,
			'CareExperience.PW.Idp': false,
			'PatientWeb.UseRelativeScrollPositionInChat': false,
			'PatientWeb.UseVonage': false,
			'CareExperience.AllowedIPList': false,
			'CareExperience.VonageForceRouted': false,
			'CareExperience.VonageE2EE': false,
			'PatientWeb.ClickableLogotype': false,
			'PatientWeb.UseNewHome': false,
			'PatientWeb.UseReceptionBackground': false,
			'PatientWeb.NHS.Navigation': false,
			'PatientWeb.HideListedAtReceptions': false,
			'PatientWeb.UseRepresentatives': false,
			'PatientWeb.NHSAppPilot': false,
			'Patient.CareCompanion.HomeChatLauncher': false,
			...ok.data,
		} satisfies Record<keyof ExpectedFlags, boolean>;

		return Ok((globalThis as typeof window).__visiba_feature_flags__);
	}

	public checkFlag(toggle: keyof T): boolean {
		const flag = FeatureFlagService.checkFlag<T>(toggle);
		if (flag.err) return false;

		return flag.ok;
	}

	public setFlag(toggle: keyof T, value: boolean) {
		FeatureFlagService.setFlag<T>(toggle, value);
	}
}

export class FeatureFlagNotSet<T extends ExpectedFlags> extends Error {
	constructor(featureFlag: keyof T) {
		super(`The feature flag "${String(featureFlag)}" has not yet been registered, was it removed?`);
		this.name = 'FeatureFlagNotSet';
		Object.setPrototypeOf(this, FeatureFlagNotSet.prototype);
	}
}

export class FeatureFlagStoreNotSet extends Error {
	constructor() {
		super(`The feature flag store has not yet been declared, has the "load" method not been invoked?`);
		this.name = 'FeatureFlagStoreNotSet';
		Object.setPrototypeOf(this, FeatureFlagStoreNotSet.prototype);
	}
}
